import { useState, useEffect, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { Plus, Trash, FloppyDisk, CheckCircle, WarningCircle,
         ArrowsClockwise, Table, Calculator, Spinner, DownloadSimple } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { inferM3 } from './mat4Volume'

// ── Estilos base ──────────────────────────────────────────────────────────────
const C = {
  bg: '#F5F0EA', card: '#FFFFFF', border: '#DDD5C8',
  dark: '#3B2F2F', brown: '#5C4A3A', muted: '#7A6A5A',
  olive: '#8D9A2E', coral: '#D4614A', amber: '#C98A2E', cream: '#FAF7F3',
  blue: '#3B7EC8',
}
const CARD  = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)' }
const INP   = { padding: '0.35rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif", color: C.dark, background: C.cream, outline: 'none', width: '100%', boxSizing: 'border-box' }
const BTN_P = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', background: C.olive, color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', fontFamily: "'DM Sans', sans-serif" }
const BTN_G = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif" }

// ── Constantes BREEAM ES MAT 4 ────────────────────────────────────────────────
const ELEMENTS_LIST = [
  { key: 'pavimentos',    label: 'Pavimentos / Losa planta baja' },
  { key: 'fachadas',      label: 'Fachadas' },
  { key: 'cubiertas',     label: 'Cubiertas' },
  { key: 'instalaciones', label: 'Instalaciones' },
  { key: 'tabiqueria',    label: 'Tabiquería y particiones' },
]

const ELEMENT_LABEL = Object.fromEntries(ELEMENTS_LIST.map(e => [e.key, e.label]))

// Niveles de certificación (Tabla 37/39 BREEAM ES NC 2015)
const TIER_INFO = [
  { value: 1, label: 'Nivel 1 – BES 6001 / FSC / PEFC', points: 3.0, color: C.olive },
  { value: 2, label: 'Nivel 2 – Esquema responsable',    points: 2.0, color: C.amber },
  { value: 3, label: 'Nivel 3 – ISO 14001 / EMAS (SGA)', points: 1.5, color: C.blue },
  { value: 4, label: 'Nivel 4 – Sin certificación',      points: 0.0, color: C.coral },
]

// Certificaciones por defecto (mismas que MAT3) — fallback si Supabase no tiene datos
const DEFAULT_CERTS = [
  { empresa: 'ARDEX',                         level: 3 },
  { empresa: 'CORTIZO',                        level: 3 },
  { empresa: 'CORTIZO 4900',                   level: 3 },
  { empresa: 'DANOSA',                         level: 3 },
  { empresa: 'FINSA',                          level: 1 },
  { empresa: 'GLASSYDUR',                      level: 3 },
  { empresa: 'ISOVER',                         level: 1 },
  { empresa: 'JOTUN',                          level: 3 },
  { empresa: 'KNAUF',                          level: 1 },
  { empresa: 'PERONDA',                        level: 4 },
  { empresa: 'PLACO SAINT GOBAIN',             level: 3 },
  { empresa: 'PORCELANOSA',                    level: 3 },
  { empresa: 'PORCELANOSA CANARIAS',           level: 3 },
  { empresa: 'PRISSMACER',                     level: 3 },
  { empresa: 'PULMOR',                         level: 3 },
  { empresa: 'PUMA',                           level: 3 },
  { empresa: 'SOPREMA',                        level: 3 },
  { empresa: 'TAUCE',                          level: 3 },
  { empresa: 'TIRSO',                          level: 3 },
  { empresa: 'SIERO LAM',                      level: 1 },
  { empresa: 'KRION PORCELANOSA',              level: 3 },
  { empresa: 'GESTORA REGIONAL DE MEDIOS SL',  level: 4 },
  { empresa: 'OLIVER CHINEA CORREA',           level: 4 },
  { empresa: 'ANDREU',                         level: 4 },
  { empresa: 'ARMACELL',                       level: 3 },
]

// ── Helpers estado ────────────────────────────────────────────────────────────
function newPartida() {
  return {
    id: Math.random(),
    code: '',
    description: '',
    material: '',
    empresa: '',
    unit: 'm3',
    qty: '',
    m3: '',
    elementBREEAM: 'pavimentos',
    pagina: '',
    source: 'manual',
  }
}

function loadState(pid) {
  try { return JSON.parse(localStorage.getItem(`mat4_${pid}`) || 'null') } catch { return null }
}
function saveState(pid, s) {
  try { localStorage.setItem(`mat4_${pid}`, JSON.stringify(s)) } catch {}
}

// ── Supabase helpers ──────────────────────────────────────────────────────────
async function savePartidasToSupabase(projectId, partidas) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr) { console.error('MAT4 savePartidas auth error:', authErr.message); return }
    if (!user) { console.error('MAT4 savePartidas: no user'); return }
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Materiales',
      criteria_code: 'MAT4-PARTIDAS',
      criteria_name: 'MAT4 Aislamiento Mediciones',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(partidas),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('MAT4 savePartidas upsert error:', error.message, error)
    else console.log('MAT4 savePartidas OK:', partidas.length)
  } catch (e) { console.error('MAT4 savePartidasToSupabase exception:', e.message) }
}

async function loadPartidasFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'MAT4-PARTIDAS').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) { /* no hay datos guardados */ }
  return null
}

async function loadMat3CertsFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'MAT3-CERTS').single()
    if (data?.justificacion) {
      const parsed = JSON.parse(data.justificacion)
      if (parsed?.length) return parsed
    }
  } catch (e) { console.warn('MAT4: no MAT3-CERTS en Supabase, usando defaults') }
  return DEFAULT_CERTS
}

// ── Dividir PDF en chunks ─────────────────────────────────────────────────────
async function splitPdf(file, maxPages = 75) {
  const buf = await file.arrayBuffer()
  const srcDoc = await PDFDocument.load(buf, { ignoreEncryption: true })
  const total = srcDoc.getPageCount()
  if (total <= maxPages) return [{ file, name: file.name, pages: total, chunkIndex: 0, chunkTotal: 1 }]
  const parts = Math.ceil(total / maxPages)
  const chunks = []
  for (let i = 0; i < parts; i++) {
    const start = i * maxPages
    const end = Math.min(start + maxPages, total)
    const chunkDoc = await PDFDocument.create()
    const indices = Array.from({ length: end - start }, (_, j) => start + j)
    const pages = await chunkDoc.copyPages(srcDoc, indices)
    pages.forEach(p => chunkDoc.addPage(p))
    const bytes = await chunkDoc.save()
    const base = file.name.replace(/\.pdf$/i, '')
    const name = `${base}_parte${i + 1}de${parts}.pdf`
    chunks.push({ file: new File([bytes], name, { type: 'application/pdf' }), name, pages: end - start, chunkIndex: i, chunkTotal: parts })
  }
  return chunks
}

// ── Cálculo MAT4 ─────────────────────────────────────────────────────────────
function calcMAT4(partidas, certMap) {
  // certMap: { empresa_lower: level }
  function getLevel(empresa, nivel) {
    if (nivel !== undefined && nivel !== null && nivel !== '') return parseInt(nivel) || 4
    if (!empresa) return 4
    const key = (empresa || '').toLowerCase().trim()
    if (certMap[key]) return certMap[key]
    for (const [k, v] of Object.entries(certMap)) {
      if (key.includes(k) || k.includes(key)) return v
    }
    return 4
  }

  // group by element
  const grouped = {}
  for (const p of partidas) {
    const el = p.elementBREEAM || 'pavimentos'
    if (!grouped[el]) grouped[el] = []
    grouped[el].push(p)
  }

  const elementResults = {}
  for (const { key } of ELEMENTS_LIST) {
    const group = grouped[key] || []
    if (!group.length) continue
    let totalM3 = 0, respM3 = 0
    let excluidasAcustico = 0, sinEspesor = 0
    for (const p of group) {
      const { m3, fuente } = inferM3(p)
      if (fuente === 'fuera_alcance') { excluidasAcustico++; continue } // acústico puro NO computa
      if (fuente === 'sin_espesor' || fuente === 'sin_inferir' || fuente === 'sin_qty') sinEspesor++
      totalM3 += m3
      if (m3 <= 0) continue
      const level = getLevel(p.empresa, p.nivel)
      if (level <= 3) respM3 += m3
    }
    const pctResp = totalM3 > 0 ? (respM3 / totalM3 * 100) : 0
    const cumple = pctResp >= 80
    elementResults[key] = { totalM3, respM3, pctResp, cumple, excluidasAcustico, sinEspesor }
  }

  const elemsWithData = Object.keys(elementResults).filter(k => elementResults[k].totalM3 > 0)
  const overallCumple = elemsWithData.length > 0 && elemsWithData.every(k => elementResults[k].cumple)
  const pts = overallCumple ? 1 : 0

  return { elementResults, overallCumple, pts }
}

// Compatibilidad con código existente que llame computeM3 directamente
function computeM3(p) {
  const { m3 } = inferM3(p)
  return m3
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function MAT4Calculator({ projectId, onSave, saving }) {
  const saved = loadState(projectId)
  const [tab, setTab] = useState('mediciones')
  const [partidas, setPartidas] = useState(() => saved?.partidas || [])
  const [mat3Certs, setMat3Certs] = useState(DEFAULT_CERTS)
  const [importing, setImporting] = useState(false)
  const [importLog, setImportLog] = useState([])
  const [batchKeyword, setBatchKeyword] = useState('')
  const [batchEmpresa, setBatchEmpresa] = useState('')
  const [batchOnlyEmpty, setBatchOnlyEmpty] = useState(true)
  const [savedOk, setSavedOk] = useState(false)
  const hasMounted = useRef(false)

  // Cargar partidas desde Supabase al montar (fuente de verdad)
  useEffect(() => {
    loadPartidasFromSupabase(projectId).then(remote => {
      if (remote) setPartidas(remote)
    })
  }, [projectId])

  // Cargar certificaciones MAT3 para autocomplete
  useEffect(() => {
    loadMat3CertsFromSupabase(projectId).then(certs => {
      if (certs?.length) setMat3Certs(certs)
    })
  }, [projectId])

  // Persistencia localStorage
  useEffect(() => {
    if (!hasMounted.current) { hasMounted.current = true; return }
    saveState(projectId, { partidas })
  }, [partidas, projectId])

  // Construir certMap desde MAT3
  const certMap = {}
  for (const c of mat3Certs) {
    if (c.empresa) certMap[c.empresa.toLowerCase().trim()] = c.level
  }

  const calc = calcMAT4(partidas, certMap)

  function saveNow() {
    saveState(projectId, { partidas })
    savePartidasToSupabase(projectId, partidas)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  async function handleSave() {
    await onSave({ pts: calc.pts, partidas })
  }

  // ── Importar presupuestos desde biblioteca ────────────────────────────────
  async function importFromLibrary() {
    setImporting(true)
    setImportLog([])
    const log = (msg) => setImportLog(prev => [...prev, msg])

    try {
      const { data: docs } = await supabase.from('project_documents')
        .select('*').eq('project_id', projectId)
      if (!docs?.length) throw new Error('No hay documentos en la biblioteca del proyecto.')

      const presupDocs = docs.filter(d =>
        d.file_name?.toLowerCase().includes('presup') ||
        d.file_name?.toLowerCase().includes('budget')
      )
      if (!presupDocs.length) throw new Error('No se encontraron archivos de presupuesto (con "Presup" en el nombre) en la biblioteca.')

      log(`Encontrados ${presupDocs.length} presupuesto(s): ${presupDocs.map(d => d.file_name).join(', ')}`)


      const allPartidas = []

      for (const doc of presupDocs) {
        const source = doc.file_name.toLowerCase().includes('a2') ? 'A2'
                     : doc.file_name.toLowerCase().includes('a3') ? 'A3' : 'General'
        log(`Procesando ${doc.file_name} (${source})...`)

        const { data: signedData } = await supabase.storage
          .from('evidences').createSignedUrl(doc.storage_path, 3600)
        if (!signedData?.signedUrl) { log(`No se pudo obtener URL de ${doc.file_name}`); continue }

        const pdfResp = await fetch(signedData.signedUrl)
        if (!pdfResp.ok) { log(`Error descargando ${doc.file_name}`); continue }

        const blob = await pdfResp.blob()
        const file = new File([blob], doc.file_name, { type: 'application/pdf' })

        log(`Dividiendo en partes de máx. 20 páginas...`)
        const chunks = await splitPdf(file, 20)
        log(`${chunks.length} parte(s) — enviando a Claude...`)

        for (const chunk of chunks) {
          const label = chunks.length > 1 ? ` parte ${chunk.chunkIndex + 1}/${chunk.chunkTotal}` : ''
          log(`  Analizando${label} (${chunk.pages} páginas)...`)

          const arrayBuffer = await chunk.file.arrayBuffer()
          const bytes = new Uint8Array(arrayBuffer)
          let binary = ''
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
          const base64 = btoa(binary)

          try {
            const { data, error } = await supabase.functions.invoke('extract-mat4-budget', {
              body: { pdfBase64: base64, fileName: doc.file_name, chunkIndex: chunk.chunkIndex, chunkTotal: chunk.chunkTotal }
            })
            if (error) throw new Error(error.message || 'Error en la Edge Function')
            if (!data?.ok) throw new Error(data?.error || 'Error desconocido en la Edge Function')
            const extracted = data.partidas || []
            if (extracted.length) {
              const tagged = extracted.map(p => ({
                ...p,
                id: Math.random(),
                source,
                empresa: p.empresa || '',
                nivel: '',
                pagina: p.pagina ?? '',
                m3: p.unit === 'm3' ? (p.qty ?? p.m3 ?? '') : (p.m3 ?? ''),
              }))
              allPartidas.push(...tagged)
              log(`  + ${extracted.length} partidas de aislamiento extraídas`)
            } else {
              log(`  Sin partidas de aislamiento en estas páginas`)
            }
          } catch (e) {
            log(`  Error en extracción: ${e.message}`)
          }
        }
      }

      if (!allPartidas.length) {
        log('No se extrajeron partidas de aislamiento. Verifica que los documentos sean presupuestos de obra.')
      } else {
        setPartidas(allPartidas)
        const current = loadState(projectId) || {}
        saveState(projectId, { ...current, partidas: allPartidas })
        savePartidasToSupabase(projectId, allPartidas)
        log(`Total: ${allPartidas.length} partidas de aislamiento extraídas. Revisa y confirma antes de guardar.`)
      }
    } catch (e) {
      log(`Error: ${e.message}`)
    }
    setImporting(false)
  }

  // ── Asignación masiva de empresa ─────────────────────────────────────────
  function batchAssign() {
    if (!batchEmpresa.trim()) return alert('Escribe una empresa para asignar.')
    const kw = batchKeyword.trim().toLowerCase()
    setPartidas(prev => {
      const updated = prev.map(p => {
        const matchDesc = !kw || (p.description || '').toLowerCase().includes(kw) || (p.material || '').toLowerCase().includes(kw)
        const matchEmpty = !batchOnlyEmpty || !p.empresa
        if (matchDesc && matchEmpty) return { ...p, empresa: batchEmpresa.trim() }
        return p
      })
      const current = loadState(projectId) || {}
      saveState(projectId, { ...current, partidas: updated })
      savePartidasToSupabase(projectId, updated)
      return updated
    })
  }

  const fmtPct = v => typeof v === 'number' ? v.toFixed(1) + '%' : '–'

  // ── Export Excel (3 hojas: Mediciones + Resumen + Empresas/Niveles) ───────
  function exportToExcel() {
    const certMap = {}
    for (const c of mat3Certs) certMap[(c.empresa || '').toLowerCase().trim()] = c.level
    function getLev(emp, niv) {
      if (niv !== undefined && niv !== null && niv !== '') return parseInt(niv) || 4
      if (!emp) return 4
      const key = emp.toLowerCase().trim()
      if (certMap[key]) return certMap[key]
      for (const [k, v] of Object.entries(certMap)) { if (key.includes(k) || k.includes(key)) return v }
      return 4
    }
    const TIER_LABEL = { 1: 'Nivel 1 (BES/FSC/PEFC)', 2: 'Nivel 2', 3: 'Nivel 3 (SGA ISO 14001)', 4: 'Nivel 4 (sin cert.)' }

    const wb = XLSX.utils.book_new()

    // Hoja 1: Mediciones
    const medRows = partidas.map(p => {
      const { m3, fuente, espesor_mm } = inferM3(p)
      const lvl = getLev(p.empresa, p.nivel)
      return {
        'Código': p.code || '',
        'Descripción': p.description || '',
        'Material': p.material || '',
        'Empresa / proveedor': p.empresa || '',
        'Elemento BREEAM': ELEMENT_LABEL[p.elementBREEAM] || p.elementBREEAM,
        'Unidad': p.unit || '',
        'Cantidad': parseFloat(p.qty) || 0,
        'm³ manual': p.m3 || '',
        'Espesor detectado (mm)': espesor_mm || '',
        'm³ inferido (cálculo BREEAM)': m3,
        'Fuente m³': fuente,
        'Nivel BREEAM': lvl,
        'Cuenta en cálculo': fuente === 'fuera_alcance' ? 'NO (acústico puro)' : (m3 > 0 ? 'SÍ' : 'NO (sin m³)'),
      }
    })
    const ws1 = XLSX.utils.json_to_sheet(medRows)
    ws1['!cols'] = [{ wch: 12 }, { wch: 60 }, { wch: 30 }, { wch: 20 }, { wch: 24 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 22 }]
    XLSX.utils.book_append_sheet(wb, ws1, 'Mediciones')

    // Hoja 2: Resumen por elemento
    const summary = ELEMENTS_LIST.map(({ key, label }) => {
      const r = calc.elementResults[key]
      if (!r) return {
        'Elemento': label, 'Nº partidas': 0, 'Total m³': 0, 'm³ responsable (L1-3)': 0,
        '% responsable': '—', 'Cumple ≥80%': 'sin datos',
        'Acústicos excluidos': 0, 'Sin espesor': 0,
      }
      return {
        'Elemento': label,
        'Nº partidas': (groupedPartidas[key] || []).length,
        'Total m³': Math.round(r.totalM3 * 100) / 100,
        'm³ responsable (L1-3)': Math.round(r.respM3 * 100) / 100,
        '% responsable': Math.round(r.pctResp * 10) / 10,
        'Cumple ≥80%': r.cumple ? 'Sí ✓' : 'No ✗',
        'Acústicos excluidos': r.excluidasAcustico || 0,
        'Sin espesor': r.sinEspesor || 0,
      }
    })
    summary.push({
      'Elemento': '— RESULTADO MAT 4 —', 'Nº partidas': partidas.length, 'Total m³': '',
      'm³ responsable (L1-3)': '', '% responsable': '',
      'Cumple ≥80%': calc.overallCumple ? '✓ TODOS pasan' : '✗ algún elemento falla',
      'Acústicos excluidos': '', 'Sin espesor': '',
    })
    summary.push({
      'Elemento': 'Puntos BREEAM', 'Nº partidas': '', 'Total m³': '',
      'm³ responsable (L1-3)': '', '% responsable': '',
      'Cumple ≥80%': `${calc.pts}/1`,
      'Acústicos excluidos': '', 'Sin espesor': '',
    })
    const ws2 = XLSX.utils.json_to_sheet(summary)
    ws2['!cols'] = [{ wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 12 }]
    XLSX.utils.book_append_sheet(wb, ws2, 'Resumen MAT 4')

    // Hoja 3: Empresas/Niveles usadas
    const empRows = []
    const seen = new Set()
    for (const p of partidas) {
      const e = (p.empresa || '').trim()
      if (!e || seen.has(e)) continue
      seen.add(e)
      const lvl = getLev(e, null)
      empRows.push({
        'Empresa / proveedor': e,
        'Nivel BREEAM (Tabla 37 MAT 3)': lvl,
        'Descripción nivel': TIER_LABEL[lvl] || '?',
        'Nº partidas en este proyecto': partidas.filter(pp => (pp.empresa || '').trim() === e).length,
      })
    }
    empRows.sort((a, b) => a['Nivel BREEAM (Tabla 37 MAT 3)'] - b['Nivel BREEAM (Tabla 37 MAT 3)'] || a['Empresa / proveedor'].localeCompare(b['Empresa / proveedor']))
    const ws3 = XLSX.utils.json_to_sheet(empRows)
    ws3['!cols'] = [{ wch: 28 }, { wch: 20 }, { wch: 28 }, { wch: 16 }]
    XLSX.utils.book_append_sheet(wb, ws3, 'Empresas y niveles')

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/octet-stream' })
    const today = new Date().toISOString().slice(0, 10)
    saveAs(blob, `MAT4_Aislamiento_${today}.xlsx`)
  }

  // Agrupación por elemento
  const groupedPartidas = {}
  for (const p of partidas) {
    const el = p.elementBREEAM || 'pavimentos'
    if (!groupedPartidas[el]) groupedPartidas[el] = []
    groupedPartidas[el].push(p)
  }

  const tabStyle = (active) => ({
    padding: '0.5rem 1rem', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: active ? '700' : '400',
    color: active ? C.olive : C.muted, background: 'transparent', borderBottom: active ? `2px solid ${C.olive}` : '2px solid transparent',
    fontFamily: "'DM Sans', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  })

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.dark }}>
      <datalist id="mat4-empresas-list">
        {mat3Certs.map(c => <option key={c.id} value={c.empresa} />)}
      </datalist>

      {/* ── Header ── */}
      <div style={{ ...CARD, marginBottom: '0.875rem', borderLeft: `4px solid ${C.amber}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: C.amber, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              BREEAM ES NC 2015 — MAT 4
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '700', marginTop: '0.15rem' }}>Aislamiento</div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', background: calc.pts > 0 ? '#F0F4E8' : '#FEF2F2', border: `1px solid ${calc.pts > 0 ? C.olive : '#FCA5A5'}`, borderRadius: '6px', padding: '0.4rem 0.75rem' }}>
              <div style={{ fontSize: '0.58rem', color: C.muted, textTransform: 'uppercase' }}>Puntos BREEAM</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: calc.pts > 0 ? C.olive : C.coral }}>
                {calc.pts}/1
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={exportToExcel} title="Descargar Excel con 3 hojas: Mediciones, Resumen MAT 4 y Empresas/Niveles" style={{ ...BTN_G, borderColor: C.blue, color: C.blue }}>
                <DownloadSimple size={14} /> Descargar Excel
              </button>
              <button onClick={saveNow} style={{ ...BTN_G, borderColor: savedOk ? C.olive : C.border, color: savedOk ? C.olive : C.muted }}>
                <FloppyDisk size={14} />{savedOk ? '+ Guardado' : 'Guardar datos'}
              </button>
              <button onClick={handleSave} disabled={saving} style={{ ...BTN_P, opacity: saving ? 0.7 : 1 }}>
                <FloppyDisk size={15} />{saving ? 'Guardando...' : 'Aplicar puntuación'}
              </button>
            </div>
          </div>
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: C.muted }}>
          1 punto si al menos el 80% (por volumen/m³) del aislamiento de CADA elemento evaluado proviene de aprovisionamiento responsable (nivel 1, 2 o 3).
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ borderBottom: `1px solid ${C.border}`, marginBottom: '0.875rem', background: C.card, borderRadius: '6px 6px 0 0', padding: '0 0.5rem' }}>
        <button style={tabStyle(tab === 'mediciones')} onClick={() => setTab('mediciones')}>
          <Table size={14} />Mediciones
        </button>
        <button style={tabStyle(tab === 'resumen')} onClick={() => setTab('resumen')}>
          <Calculator size={14} />Resumen
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 1 — MEDICIONES
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'mediciones' && (
        <div>
          {/* Barra de acciones */}
          <div style={{ ...CARD, marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={importFromLibrary} disabled={importing} style={{ ...BTN_P, opacity: importing ? 0.7 : 1 }}>
              {importing ? <Spinner size={14} className="spin" /> : <ArrowsClockwise size={14} />}
              {importing ? 'Importando…' : 'Importar presupuestos de la biblioteca'}
            </button>
            <button onClick={() => setPartidas(prev => [...prev, newPartida()])} style={BTN_G}>
              <Plus size={13} />Añadir partida manual
            </button>
            <button onClick={saveNow} style={{ ...BTN_G, borderColor: savedOk ? C.olive : C.border, color: savedOk ? C.olive : C.muted }}>
              <FloppyDisk size={13} />{savedOk ? 'Guardado' : 'Guardar datos'}
            </button>
          </div>

          {/* Log de importación */}
          {importLog.length > 0 && (
            <div style={{ ...CARD, marginBottom: '0.75rem', background: '#1E1E1E', color: '#C8F0C8', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', padding: '0.75rem 1rem', maxHeight: '160px', overflowY: 'auto' }}>
              {importLog.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          )}

          {/* Asignación masiva */}
          {partidas.length > 0 && (
            <div style={{ ...CARD, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Asignar empresa por lotes</span>
              <input
                value={batchKeyword}
                onChange={e => setBatchKeyword(e.target.value)}
                placeholder="Filtrar por palabra (ej: lana mineral)"
                style={{ ...INP, flex: '1', minWidth: '140px' }}
              />
              <input
                list="mat4-empresas-list"
                value={batchEmpresa}
                onChange={e => setBatchEmpresa(e.target.value)}
                placeholder="Empresa a asignar"
                style={{ ...INP, flex: '1', minWidth: '140px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: C.muted, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={batchOnlyEmpty} onChange={e => setBatchOnlyEmpty(e.target.checked)} />
                Solo vacíos
              </label>
              <button onClick={batchAssign} style={{ ...BTN_G, color: C.olive, borderColor: C.olive, whiteSpace: 'nowrap' }}>
                Asignar →
              </button>
            </div>
          )}

          {/* Tabla de partidas */}
          {partidas.length === 0 ? (
            <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem', color: C.muted }}>
              <Table size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
              <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>Sin datos de aislamiento</div>
              <div style={{ fontSize: '0.75rem' }}>Importa los presupuestos desde la biblioteca o añade partidas manualmente.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr style={{ background: C.cream }}>
                    {['Página', 'Elemento BREEAM', 'Código', 'Descripción', 'Material', 'Unidad', 'Cantidad', 'M³', 'Fabricante', 'Nivel', ''].map((h, i) => (
                      <th key={i} style={{ padding: '0.45rem 0.5rem', textAlign: 'left', fontWeight: '600', color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partidas.map((p) => {
                    const m3val = computeM3(p)
                    const certLevel = (() => {
                      if (p.nivel !== undefined && p.nivel !== null && p.nivel !== '') return parseInt(p.nivel) || 4
                      if (!p.empresa) return 4
                      const key = (p.empresa || '').toLowerCase().trim()
                      if (certMap[key]) return certMap[key]
                      for (const [k, v] of Object.entries(certMap)) {
                        if (key.includes(k) || k.includes(key)) return v
                      }
                      return 4
                    })()
                    const tierInfo = TIER_INFO.find(t => t.value === certLevel)
                    const updateP = (field, val) => setPartidas(prev => prev.map(x => x.id === p.id ? { ...x, [field]: val } : x))
                    const saveP = () => setPartidas(prev => {
                      const current = loadState(projectId) || {}
                      saveState(projectId, { ...current, partidas: prev })
                      savePartidasToSupabase(projectId, prev)
                      return prev
                    })
                    return (
                      <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input
                            type="number"
                            value={p.pagina ?? ''}
                            onChange={e => updateP('pagina', e.target.value)}
                            onBlur={saveP}
                            placeholder="p."
                            style={{ ...INP, width: '55px', textAlign: 'center' }}
                          />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <select value={p.elementBREEAM || 'pavimentos'} onChange={e => updateP('elementBREEAM', e.target.value)} style={{ ...INP, width: '140px' }}>
                            {ELEMENTS_LIST.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input value={p.code || ''} onChange={e => updateP('code', e.target.value)} placeholder="00.00.00" style={{ ...INP, width: '90px' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input value={p.description || ''} onChange={e => updateP('description', e.target.value)} placeholder="Descripción" style={{ ...INP, width: '180px' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input value={p.material || ''} onChange={e => updateP('material', e.target.value)} placeholder="Lana mineral, XPS..." style={{ ...INP, width: '140px' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <select value={p.unit || 'm3'} onChange={e => updateP('unit', e.target.value)} style={{ ...INP, width: '65px' }}>
                            {['m3', 'm2', 'm', 'ml', 'ud', 'kg'].map(u => <option key={u}>{u}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input type="number" value={p.qty ?? ''} onChange={e => updateP('qty', e.target.value)} placeholder="0" style={{ ...INP, width: '75px', textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          {p.unit === 'm3' ? (
                            <span style={{ fontSize: '0.75rem', color: C.muted, padding: '0.35rem 0.6rem', display: 'block', textAlign: 'right' }}>
                              {m3val > 0 ? m3val.toFixed(2) : '—'}
                            </span>
                          ) : (
                            <input type="number" value={p.m3 ?? ''} onChange={e => updateP('m3', e.target.value)} onBlur={saveP}
                              placeholder="m³" style={{ ...INP, width: '75px', textAlign: 'right' }} />
                          )}
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <input
                            list="mat4-empresas-list"
                            value={p.empresa || ''}
                            onChange={e => updateP('empresa', e.target.value)}
                            onBlur={saveP}
                            placeholder="— asignar —"
                            style={{ ...INP, width: '120px', borderColor: !p.empresa ? '#E8A838' : INP.borderColor, background: !p.empresa ? '#FFFBF0' : INP.background }}
                          />
                        </td>
                        <td style={{ padding: '0.3rem 0.4rem' }}>
                          <select
                            value={p.nivel !== undefined && p.nivel !== null && p.nivel !== '' ? p.nivel : certLevel}
                            onChange={e => updateP('nivel', e.target.value === String(certLevel) && !p.nivel ? '' : e.target.value)}
                            style={{ ...INP, width: '75px', color: tierInfo?.color || C.muted, fontWeight: '700' }}
                          >
                            {TIER_INFO.map(t => <option key={t.value} value={t.value} style={{ color: TIER_INFO.find(x => x.value === t.value)?.color }}>N{t.value}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.3rem 0.3rem' }}>
                          <button onClick={() => {
                            setPartidas(prev => {
                              const updated = prev.filter(x => x.id !== p.id)
                              const current = loadState(projectId) || {}
                              saveState(projectId, { ...current, partidas: updated })
                              savePartidasToSupabase(projectId, updated)
                              return updated
                            })
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.coral, padding: 0, display: 'flex' }}>
                            <Trash size={13} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 2 — RESUMEN
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'resumen' && (
        <div>
          {/* Tabla resumen por elemento */}
          <div style={{ ...CARD, marginBottom: '0.875rem', padding: 0 }}>
            <div style={{ padding: '0.6rem 1rem', borderBottom: `1px solid ${C.border}`, fontSize: '0.7rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Resumen por Elemento de Construcción
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: C.cream }}>
                  {['Elemento', 'Total m³', 'm³ Responsable', '% Responsable', 'Cumple (≥80%)'].map((h, i) => (
                    <th key={i} style={{ padding: '0.45rem 0.75rem', textAlign: i >= 1 ? 'right' : 'left', fontWeight: '600', color: C.muted, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ELEMENTS_LIST.map(({ key, label }) => {
                  const r = calc.elementResults[key]
                  if (!r) {
                    return (
                      <tr key={key} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '0.4rem 0.75rem', color: C.muted }}>{label}</td>
                        <td colSpan={4} style={{ padding: '0.4rem 0.75rem', textAlign: 'right', color: C.muted, fontSize: '0.73rem' }}>Sin partidas</td>
                      </tr>
                    )
                  }
                  return (
                    <tr key={key} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '0.4rem 0.75rem', fontWeight: '600' }}>{label}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{r.totalM3.toFixed(2)}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", color: r.cumple ? C.olive : C.coral }}>{r.respM3.toFixed(2)}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: '700', color: r.cumple ? C.olive : C.coral }}>{fmtPct(r.pctResp)}</td>
                      <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right' }}>
                        {r.cumple
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: C.olive, fontWeight: '700', fontSize: '0.75rem' }}><CheckCircle size={15} weight="fill" />Sí</span>
                          : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: C.coral, fontWeight: '700', fontSize: '0.75rem' }}><WarningCircle size={15} weight="fill" />No</span>
                        }
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Resultado global */}
          <div style={{ ...CARD, marginBottom: '0.875rem', borderLeft: `4px solid ${calc.overallCumple ? C.olive : C.coral}`, background: calc.overallCumple ? '#F6FAF0' : '#FEF7F7' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: calc.overallCumple ? C.olive : C.coral, marginBottom: '0.2rem' }}>
                  Resultado MAT 4
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: C.dark }}>
                  {calc.overallCumple
                    ? 'Criterio Cumplido — todos los elementos alcanzan el 80% de aislamiento responsable'
                    : Object.keys(calc.elementResults).length === 0
                      ? 'Sin datos — añade partidas de aislamiento en la pestaña Mediciones'
                      : 'Criterio No Cumplido — algún elemento no alcanza el 80% de aislamiento responsable'
                  }
                </div>
              </div>
              <div style={{ textAlign: 'center', background: calc.pts > 0 ? '#F0F4E8' : '#FEF2F2', border: `1px solid ${calc.pts > 0 ? C.olive : '#FCA5A5'}`, borderRadius: '8px', padding: '0.5rem 1.25rem' }}>
                <div style={{ fontSize: '0.6rem', color: C.muted, textTransform: 'uppercase' }}>Puntos BREEAM</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: calc.pts > 0 ? C.olive : C.coral }}>{calc.pts}/1</div>
              </div>
            </div>
          </div>

          {/* Leyenda niveles */}
          <div style={{ ...CARD }}>
            <div style={{ fontSize: '0.68rem', fontWeight: '700', color: C.brown, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
              Niveles de certificación aplicables (Tabla 39 BREEAM ES NC 2015)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem' }}>
              {TIER_INFO.map(t => (
                <div key={t.value} style={{ padding: '0.4rem 0.6rem', background: C.cream, borderRadius: '4px', borderLeft: `3px solid ${t.color}`, fontSize: '0.71rem' }}>
                  <strong style={{ color: t.color }}>{t.points} pts</strong> — {t.label}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '0.6rem', fontSize: '0.7rem', color: C.muted }}>
              Materiales responsables: niveles 1, 2 y 3. Nivel 4 (sin certificación) no cuenta como responsable.
            </div>
          </div>

          {/* Botón Aplicar puntuación */}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleSave} disabled={saving} style={{ ...BTN_P, opacity: saving ? 0.7 : 1, padding: '0.6rem 1.5rem', fontSize: '0.85rem' }}>
              <FloppyDisk size={16} />{saving ? 'Guardando...' : 'Aplicar puntuación'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
